import { SectionId } from '@wix/wix-help-widget-common/homepage';
import React from 'react';
import { useStore } from 'react-redux';

import { useOnVisible } from '../../common/hooks/useOnVisible';
import { selectHomepageContent } from '../../common/selectors/homepage';
import { biLogger } from '../biLogger';
import { createHomepageSolutionInfo } from '../solutionInfo';
import { ButtonClicked } from '../types/buttonClicked';
import { getSharedBiFieldsFromState } from '../utils/sharedBiFields';

export enum SectionEventSource {
  EXTERNAL_RESOURCES = 'EXTERNAL_RESOURCES',
  HIRE_PARTNER = 'HIRE_PARTNER',
  CONTACT_US = 'CONTACT_US',
  STATUS_PAGE = 'STATUS_PAGE',
  STATUS_BANNER = 'STATUS_BANNER',
  TERMS_OF_USE_AND_PRIVACY_POLICY = 'TERMS_OF_USE_AND_PRIVACY_POLICY',
  CHAT_WITH_US = 'CHAT_WITH_US',
  USER_CONTACTS = 'USER_CONTACTS',
  SHARE_FEEDBACK = 'SHARE_FEEDBACK',
  SEARCH_SUGGESTIONS = 'SEARCH_SUGGESTIONS',
  KERA_ASSISTANT = 'KERA_ASSISTANT',
}

const sectionIdToEventSource: Record<SectionId, SectionEventSource> = {
  [SectionId.Resources]: SectionEventSource.EXTERNAL_RESOURCES,
  [SectionId.HirePartner]: SectionEventSource.HIRE_PARTNER,
  [SectionId.ContactUs]: SectionEventSource.CONTACT_US,
  [SectionId.StatusPage]: SectionEventSource.STATUS_PAGE,
  [SectionId.TermsOfUseAndPrivacyPolicy]: SectionEventSource.TERMS_OF_USE_AND_PRIVACY_POLICY,
  [SectionId.ChatWithUs]: SectionEventSource.CHAT_WITH_US,
  [SectionId.UserContacts]: SectionEventSource.USER_CONTACTS,
  [SectionId.ShareFeedback]: SectionEventSource.SHARE_FEEDBACK,
  [SectionId.KeraAssistant]: SectionEventSource.KERA_ASSISTANT,
};

const eventSourceToItemName: Record<SectionEventSource, string> = {
  [SectionEventSource.EXTERNAL_RESOURCES]: 'external_resources',
  [SectionEventSource.HIRE_PARTNER]: ButtonClicked.HirePartner,
  [SectionEventSource.CONTACT_US]: ButtonClicked.ContactUs,
  [SectionEventSource.STATUS_PAGE]: ButtonClicked.StatusPage,
  [SectionEventSource.STATUS_BANNER]: ButtonClicked.StatusBanner,
  [SectionEventSource.TERMS_OF_USE_AND_PRIVACY_POLICY]: ButtonClicked.TermsOfUse,
  [SectionEventSource.CHAT_WITH_US]: ButtonClicked.ChatWithUs,
  [SectionEventSource.USER_CONTACTS]: ButtonClicked.ContactPartnerSuccessTeam,
  [SectionEventSource.SHARE_FEEDBACK]: ButtonClicked.ReportBug,
  [SectionEventSource.SEARCH_SUGGESTIONS]: ButtonClicked.SearchSuggestedQuestion,
  [SectionEventSource.KERA_ASSISTANT]: 'kera_voice_assistant_cta',
};

export const useBiOnVisibleItem = <T extends HTMLElement = HTMLDivElement>(
  sectionId: SectionId | SectionEventSource,
  itemId?: string,
  buttonName?: string,
) => {
  const itemRef = React.useRef<T>(null);
  const [isEventSent, setIsEventSent] = React.useState(false);
  const store = useStore();
  const isVisible = useOnVisible(itemRef);

  const eventSource = typeof sectionId === 'string' ? sectionId : sectionIdToEventSource[sectionId];

  React.useEffect(() => {
    setIsEventSent(false);
  }, [sectionId, itemId, buttonName]);

  const sendItemOnViewEvent = React.useCallback(async () => {
    const state = store.getState();
    const homepageContent = selectHomepageContent(state);
    const sharedBiFields = await getSharedBiFieldsFromState(state);

    const presentedContent = createHomepageSolutionInfo(homepageContent.data);

    const itemName = itemId
      ? eventSource === SectionEventSource.EXTERNAL_RESOURCES
        ? buttonName || `external_resource_${itemId}`
        : eventSource === SectionEventSource.SHARE_FEEDBACK
          ? buttonName || itemId
          : ButtonClicked.SearchSuggestedQuestion
      : eventSourceToItemName[eventSource];

    const baseEventData = {
      widget_language: sharedBiFields.widget_language,
      self_service_session_id: sharedBiFields.self_service_session_id,
      widget_page_type: 'home',
      presented_content: JSON.stringify(presentedContent),
    };

    if (eventSource === SectionEventSource.TERMS_OF_USE_AND_PRIVACY_POLICY) {
      await Promise.all([
        biLogger.helpWidgetItemOnViewNew(state, {
          ...baseEventData,
          item_name: ButtonClicked.TermsOfUse,
        }),
        biLogger.helpWidgetItemOnViewNew(state, {
          ...baseEventData,
          item_name: ButtonClicked.PrivacyPolicy,
        }),
      ]);
    } else {
      await biLogger.helpWidgetItemOnViewNew(state, {
        ...baseEventData,
        item_name: itemName,
      });
    }

    setIsEventSent(true);
  }, [store, eventSource, itemId, buttonName]);

  React.useEffect(() => {
    if (isVisible && !isEventSent) {
      sendItemOnViewEvent().catch(() => 0);
    }
  }, [isVisible, isEventSent, sendItemOnViewEvent]);

  const cancelItemOnViewEvent = React.useCallback(() => {
    setIsEventSent(true);
  }, []);

  const resetItemOnViewEvent = React.useCallback(() => {
    setIsEventSent(false);
  }, []);

  return {
    cancelItemOnViewEvent,
    resetItemOnViewEvent,
    itemRef,
  };
};
